import { Form } from "@inertiajs/react"
import { Check, Loader2 } from "lucide-react"
import { type ReactElement, useState } from "react"
import { useTranslation } from "react-i18next"

import { BadgeTile } from "@/components/badges/badge-tile"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { badgeAwardsPath } from "@/routes"

/**
 * OKS-50 — Badge award modal.
 *
 * Triggered from the catalogue tile's "Award" button. Body is a
 * shadcn `Command` (cmdk) recipient picker — multi-select, with
 * already-awarded creators rendered disabled.
 *
 * Form posts to `POST /badges/:id/awards` with repeated
 * `crew_membership_ids[]` hidden inputs (Rails `Array(params[...])`
 * coerces). Idempotent server-side via the partial unique
 * `(membership, badge_id)` index.
 */
interface BadgeAwardModalProps {
  trigger: ReactElement
  badge: {
    id: number
    name: string
    description: string
    icon: string
    accent_color: string
    image_url: string | null
  }
  /** Brand crew memberships eligible for award. */
  candidates: {
    membership_id: number
    name: string
    handle: string | null
    already_earned: boolean
  }[]
  /** When triggered from `/crew/:id`, optional return URL hint. */
  returnTo?: string
}

export function BadgeAwardModal({
  trigger,
  badge,
  candidates,
  returnTo,
}: BadgeAwardModalProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<Set<number>>(new Set())

  const toggle = (id: number, alreadyEarned: boolean) => {
    if (alreadyEarned) return
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) setSelected(new Set())
      }}
    >
      <DialogTrigger render={trigger} />
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("badges.dialog.award_title", { badge: badge.name })}
          </DialogTitle>
        </DialogHeader>

        <div className="bg-muted/40 rounded-lg border p-3">
          <BadgeTile
            variant="image"
            imageUrl={badge.image_url}
            icon={badge.icon}
            accentColor={badge.accent_color}
            name={badge.name}
            hint={badge.description}
            // OKS-937 — no state word: this badge is about to be awarded,
            // so nobody has earned it yet. `earnedAt` is styling only.
            ariaLabel={t("badges.aria.tile_catalogue", {
              name: badge.name,
              description: badge.description,
            })}
            earnedAt={new Date().toISOString()}
          />
        </div>

        <Form
          action={badgeAwardsPath(badge.id)}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
          data-testid="badge-award-form"
          className="flex flex-col gap-3"
        >
          {({ processing }) => (
            <>
              {Array.from(selected).map((id) => (
                <input
                  key={id}
                  type="hidden"
                  name="crew_membership_ids[]"
                  value={id}
                />
              ))}
              {returnTo && (
                <input type="hidden" name="return_to" value={returnTo} />
              )}

              <Command
                className="rounded-md border"
                filter={(value, search) => {
                  return value.toLowerCase().includes(search.toLowerCase())
                    ? 1
                    : 0
                }}
              >
                <CommandInput
                  placeholder={t("badges.award_modal.recipient_label")}
                />
                <CommandList>
                  <CommandEmpty>
                    {t("badges.award_modal.no_matches")}
                  </CommandEmpty>
                  {candidates.length === 0 ? (
                    <p className="text-muted-foreground p-3 text-sm">
                      {t("badges.award_modal.empty")}
                    </p>
                  ) : (
                    <CommandGroup>
                      {candidates.map((c) => {
                        const checked = selected.has(c.membership_id)
                        const disabled = c.already_earned
                        return (
                          <CommandItem
                            key={c.membership_id}
                            value={`${c.name} ${c.handle ?? ""}`}
                            disabled={disabled}
                            onSelect={() => toggle(c.membership_id, disabled)}
                            data-testid={`award-candidate-${c.membership_id}`}
                          >
                            <span className="flex flex-1 items-center gap-2">
                              {checked && (
                                <Check className="h-4 w-4" aria-hidden="true" />
                              )}
                              <span>{c.name}</span>
                              {c.handle && (
                                <span className="text-muted-foreground text-xs">
                                  @{c.handle}
                                </span>
                              )}
                            </span>
                            {disabled && (
                              <span className="text-muted-foreground text-xs">
                                {t("badges.award_modal.already_earned")}
                              </span>
                            )}
                          </CommandItem>
                        )
                      })}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>

              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={processing || selected.size === 0}
                  data-testid="award-submit"
                >
                  {processing && (
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  {t("badges.actions.award")}
                </Button>
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
